import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { getIsWhitelabel } from 'src/store/view/selectors';

import {
  AuthFormType,
  PageType,
  RequestStatus,
  ClientType,
} from 'src/components/AuthNew/types';
import { getClientType } from 'src/components/AuthNew/utils/getClientType';
import type { SharedState } from 'src/components/AuthNew/types';
import {
  ClientUpdateForm,
  MailConfirmation,
} from 'src/routes/Checkout/components/Client/components';
import {
  useEmailWasChanged,
  useStateNewPhone,
  useClientFormStepState,
  useUpdateClient,
} from 'src/routes/Checkout/components/Client/hooks';
import { useConfirmPhone, useGetNewCode } from 'src/components/AuthNew/hooks';

import { ClientFormStep } from 'src/routes/Checkout/components/Client/types';
import type { FullClientData } from '@lt/api/dist/types/client';
import { SmsCodeForm } from 'src/components/AuthNew/components/SmsCodeForm';
import { setPhoneFormat } from 'src/components/AuthNew/utils';
import { useTranslation } from '@lt/localization';
import ReCaptchaModal from 'src/components/ReCaptchaModal';
import { AuthNew } from '../../../components/AuthNew';
import withMobile from '../hocs/withMobile';

const authLayoutCSS = {
  [AuthFormType.Login]: {
    loginFormCSS: css`
      background: var(--white);
    `,
  },
  [AuthFormType.Registration]: {
    isStyleAsMobile: true,
    containerCSS: css`
      width: 100%;
    `,
    wrapperCSS: css`
      width: 100%;
    `,
  },
  [AuthFormType.LoginWithPassword]: {
    containerCSS: css`
      width: 290px;
    `,
    wrapperCSS: css`
      width: 100%;
    `,
  },
  [AuthFormType.LoginWithSMSCode]: {
    containerCSS: css`
      width: 290px;
    `,
    wrapperCSS: css`
      width: 100%;
    `,
  },
};

const Client = ({
  isLoggedIn,
  unconfirmedPhone,
  clientData,
  clientAuthToken,
  setClientInfo,
  confirmClient,
  setConfirmedPhone,
  isConfirmedClientEmail,
  logIn,
  logOut,
  signUp,
  isMobile,
}) => {
  const initialSharedState: SharedState = useMemo(
    () => ({
      phone: clientData?.phone ?? '',
      unconfirmed_phone: unconfirmedPhone,
      phone_mask: '',
      country: '',
      email: clientData?.email ?? '',
      name: clientData?.name ?? '',
      surname: clientData?.surname ?? '',
      password: '',
      signUpType: null,
    }),
    [clientData, unconfirmedPhone],
  );
  const { t } = useTranslation();

  const [requestStatus, setRequestStatus] = useState(RequestStatus.Idle);
  const {
    clientFormStep,
    openConfirmationPhone,
    openMailConfirmation,
    openSendSmsCodeAgainForm,
  } = useClientFormStepState(isLoggedIn);

  const [sharedState, setSharedState] =
    useState<SharedState>(initialSharedState);
  const { emailWasChangedRef, setEmailWasChanged } = useEmailWasChanged();
  const { newPhone, setNewPhone } = useStateNewPhone();
  const {
    timeLeftToDeliver,
    handleGetNewCode,
    timer,
    isOpenReCaptcha,
    captchaCallback,
  } = useGetNewCode();

  const [clientFormType, setClientFormType] =
    useState<ClientFormStep>(clientFormStep);

  useEffect(() => {
    setClientFormType(clientFormStep);
  }, [clientFormStep]);

  const isWhiteLabel = useSelector(getIsWhitelabel);

  const { submitUpdateClient } = useUpdateClient({
    clientAuthToken,
    clientData,
    setClientInfo,
    setRequestStatus,
    setEmailWasChanged,
    setNewPhone,
    openMailConfirmation,
    openConfirmationPhone,
    confirmClient,
    handleGetNewCode,
  });

  const confirmFormFuncs = {
    onSuccessConfirmPhone: (authNewState?) => {
      if (emailWasChangedRef.current) {
        openMailConfirmation();
      } else {
        const clientDataWithNewPhone = {
          ...clientData,
          ...authNewState,
          phone:
            newPhone ||
            sharedState.unconfirmed_phone ||
            authNewState?.unconfirmed_phone,
          unconfirmed_phone: null,
          auth_token: clientAuthToken,
        } as FullClientData;

        openMailConfirmation();

        /** сохранение подтвержденного телефона в стор */
        setClientInfo(clientDataWithNewPhone);
      }
    },
    onSendSmsCodeAgain: openSendSmsCodeAgainForm,
    setRequestStatus,
  };

  const { handleConfirmPhone } = useConfirmPhone({
    onSuccessConfirmPhone: confirmFormFuncs.onSuccessConfirmPhone,
    onSendSmsCodeAgain: confirmFormFuncs.onSendSmsCodeAgain,
    setRequestStatus: confirmFormFuncs.setRequestStatus,
  });

  const onSignIn = useCallback(
    (client) => {
      setTimeout(() => {
        logIn(client);
      }, 2000);
    },
    [logIn],
  );

  const onGetClientType = getClientType(
    isLoggedIn,
    isConfirmedClientEmail,
    !!unconfirmedPhone,
  );

  function initialFormType() {
    if (clientData?.id && !!unconfirmedPhone) {
      return AuthFormType.SetPhoneForConfirm;
    }
    return AuthFormType.Login;
  }

  const isClientUpdateForm =
    clientFormType === ClientFormStep.CLIENT_INFO &&
    clientData &&
    (onGetClientType === ClientType.ConfEmailConfPhone ||
      onGetClientType === ClientType.UnconfEmailConfPhone);

  return (
    <StyledContainer isMobile={isMobile}>
      {isClientUpdateForm && (
        <ClientUpdateForm
          sharedState={sharedState}
          setSharedState={setSharedState}
          onSubmit={submitUpdateClient}
          requestStatus={requestStatus}
          client={clientData}
          submitText={t('checkout.updateForm.submit')}
          isStyleAsMobile
        />
      )}
      {clientFormType === ClientFormStep.MAIL_CONFIRMATION && (
        <MailConfirmation submitMailConfirmation={confirmClient} />
      )}
      {(clientFormType === ClientFormStep.AUTH ||
        (clientFormType === ClientFormStep.CLIENT_INFO &&
          !isClientUpdateForm)) && (
        <AuthNew
          isMobile={isMobile}
          isWhiteLabel={isWhiteLabel}
          authLayoutCSS={authLayoutCSS}
          onSignIn={onSignIn}
          onSignUp={signUp}
          onLogOut={logOut}
          onClose={() => null}
          setConfirmedPhone={setConfirmedPhone}
          clientType={onGetClientType}
          pageType={PageType.Gift}
          initialFormType={initialFormType()}
          outsideSharedState={sharedState}
        />
      )}
      {clientFormType === ClientFormStep.PHONE_CONFIRMATION && (
        <SmsCodeForm
          timeLeftToDeliver={timeLeftToDeliver}
          timer={timer}
          clientId={clientData?.id}
          onGetNewCode={handleGetNewCode}
          requestStatus={requestStatus}
          switchForm={() => null}
          phone={setPhoneFormat(
            sharedState.phone_mask ?? '',
            newPhone || sharedState.unconfirmed_phone || '',
          )}
          isConfirmPhoneProcess
          isAuthProcess={false}
          onConfirmPhone={handleConfirmPhone}
        />
      )}
      <ReCaptchaModal
        isOpened={isOpenReCaptcha}
        onCaptchaPassed={captchaCallback}
      />
    </StyledContainer>
  );
};
const StyledContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  form {
    background: ${(props) => props.theme.colors.veryLightPink};
    .lt-row {
      margin-top: 0;
      margin-right: 0;
      margin-left: 0;
      width: 100%;
      flex-wrap: wrap;

      div,
      input {
        width: 100%;
      }

      .lt-form-textfield {
        margin-right: 0;
        margin-left: 0;
      }
    }

    .lt-auth-form__row {
      display: flex;
      justify-content: center;
    }
  }

  ${({ isMobile }) =>
    isMobile &&
    css`
      .lt-auth-social__button {
        width: 140px;
      }

      .lt-auth-client-form__back {
        white-space: pre;
      }
    `};
`;

Client.defaultProps = {
  clientData: null,
};

Client.propTypes = {
  logIn: PropTypes.func.isRequired,
  clientData: PropTypes.shape({}),
};

export default withMobile(Client);
